import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import LaTeXRenderer from '../LaTeXRenderer';
import { useAuth } from '../../contexts/AuthContext';
import ScoreMatrix from '../shared/ScoreMatrix';

const StudentDashboard = ({ user: propUser, hasGraderAccess }) => {
  const { user: authUser } = useAuth();
  const user = propUser || authUser;
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('available'); // State for tab management

  useEffect(() => {
    // Debug logging to see actual user data
    console.log('🔍 StudentDashboard Debug - User object:', user);
    console.log('🔍 User status:', user?.status);
    console.log('🔍 User role:', user?.role);
    console.log('🔍 Is pending approval?', user?.status === 'pending_approval');
    
    // Force console output and add alert for testing
    if (user) {
      console.log('🚀 USER FOUND - Debug Info:');
      console.log('  - User ID:', user._id);
      console.log('  - User Name:', user.name);
      console.log('  - User Email:', user.email);
      console.log('  - User Status:', user.status);
      console.log('  - User Role:', user.role);
      
      // Temporary alert to test if useEffect is running
      // alert(`Debug: User status is "${user.status}" and role is "${user.role}"`);
    } else {
      console.log('❌ NO USER OBJECT FOUND');
      // alert('Debug: No user object found');
    }
    
    // Check if user account is pending approval
    if (user?.status === 'pending_approval') {
      console.log('✅ Showing pending approval message');
      setLoading(false);
      return;
    }
    
    console.log('📋 Fetching tests for approved user');
    fetchTests();
  }, [user]);

  const fetchTests = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tests`);
      setTests(response.data);
    } catch (error) {
      setError('Failed to load tests');
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (test) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${test._id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${test.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download PDF');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isOverdue = (dueDate) => {
    return new Date() > new Date(dueDate);
  };

  const getStatusInfo = (test) => {
    if (test.status === 'closed') {
      return { 
        badge: { text: 'Closed', color: '#ffc107', textColor: 'black' },
        message: 'Submissions closed - view feedback only',
        canSubmit: false 
      };
    }
    if (test.status === 'active' && isOverdue(test.dueDate)) {
      return { 
        badge: { text: 'Overdue', color: '#dc3545', textColor: 'white' },
        message: 'Past due date',
        canSubmit: true 
      };
    }
    return { 
      badge: null,
      message: null,
      canSubmit: test.status === 'active' 
    };
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* DEBUG SECTION - Remove this after debugging */}
      {/* 
      <div className="card" style={{ backgroundColor: '#e7f3ff', border: '2px solid #0066cc' }}>
        <h3 style={{ color: '#0066cc' }}>🐛 DEBUG INFO (Remove after fixing)</h3>
        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          <p><strong>User Object:</strong> {user ? 'EXISTS' : 'NULL/UNDEFINED'}</p>
          {user && (
            <>
              <p><strong>User ID:</strong> {user._id || 'Not available'}</p>
              <p><strong>User Name:</strong> {user.name || 'Not available'}</p>
              <p><strong>User Email:</strong> {user.email || 'Not available'}</p>
              <p><strong>User Status:</strong> {user.status || 'Not available'}</p>
              <p><strong>User Role:</strong> {user.role || 'Not available'}</p>
              <p><strong>Is Pending Approval:</strong> {user.status === 'pending_approval' ? 'YES' : 'NO'}</p>
            </>
          )}
          <p><strong>Loading State:</strong> {loading ? 'TRUE' : 'FALSE'}</p>
          <p><strong>Error State:</strong> {error || 'None'}</p>
          <p><strong>Tests Count:</strong> {tests.length}</p>
        </div>
      </div>
      */}

      <div className="card">
        <h1>Student Dashboard</h1>
        <h3>Welcome, {user?.name}! 👋</h3>
        <p>Welcome to the student portal. Here you can view and submit solutions for weekly mock tests.</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* Show pending approval message instead of tests for pending accounts */}
      {user?.status === 'pending_approval' ? (
        <div className="card" style={{ 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeeba',
          borderRadius: '8px'
        }}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <h2 style={{ color: '#856404', marginBottom: '16px' }}>Account Pending Approval</h2>
            <div style={{ 
              backgroundColor: 'white', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #ffeeba',
              marginBottom: '20px'
            }}>
              <p style={{ fontSize: '16px', color: '#495057', marginBottom: '16px', lineHeight: '1.6' }}>
                Thank you for registering with IMO Sprint!
              </p>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  marginTop: '16px',
                  padding: '20px', 
                  backgroundColor: '#f8d7da', 
                  border: '1px solid #f5c6cb',
                  borderRadius: '8px',
                  color: '#721c24',
                  lineHeight: '1.6'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '16px' }}>📧 Please contact IMO Sprint organizers:</div>
                  <div style={{ marginBottom: '8px' }}>• Hongzhou Lin: <a href="mailto:hongzhou.lin89@gmail.com" style={{ color: '#0066cc', textDecoration: 'underline' }}>hongzhou.lin89@gmail.com</a></div>
                  <div style={{ marginBottom: '8px' }}>• Kritesh Dhakal: <a href="mailto:kriteshdhakal314159@gmail.com" style={{ color: '#0066cc', textDecoration: 'underline' }}>kriteshdhakal314159@gmail.com</a></div>
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f5c6cb' }}>
                    <strong>Program Enrollment:</strong> <a href="https://www.patreon.com/c/imo_sprint/membership" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>Join us through Patreon!</a>
                  </div>
                </div>
              </div>

              <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px', lineHeight: '1.5' }}>
                <strong>Next Steps to Complete Your Registration:</strong>
              </p>
              <ul style={{ 
                textAlign: 'left', 
                color: '#6c757d', 
                fontSize: '14px',
                lineHeight: '1.5',
                paddingLeft: '20px',
                marginBottom: '16px'
              }}>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Step 1:</strong> Enroll in IMO Sprint through our Patreon page (required for account activation)
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Step 2:</strong> Contact the organizers above to confirm your Patreon enrollment
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Step 3:</strong> We'll activate your account within 24-48 hours after enrollment verification
                </li>
              </ul>
              <p style={{ fontSize: '14px', color: '#856404', fontStyle: 'italic' }}>
                Please check your email regularly for approval notification. If you have any questions, 
                feel free to contact our support team.
              </p>
            </div>
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '6px',
              border: '1px solid #dee2e6'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>
                <strong>Note:</strong> You can still access your profile and browse the platform, 
                but mock tests and submissions will be available after approval.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <h2 style={{ marginBottom: '20px' }}>Mock Tests</h2>
          
          {/* Tab Navigation */}
          <div style={{ 
            display: 'flex', 
            borderBottom: '2px solid #e9ecef', 
            marginBottom: '24px',
            gap: '0'
          }}>
            <button
              onClick={() => setActiveTab('available')}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderBottom: activeTab === 'available' ? '3px solid #007bff' : '3px solid transparent',
                backgroundColor: 'transparent',
                color: activeTab === 'available' ? '#007bff' : '#6c757d',
                fontWeight: activeTab === 'available' ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Available Mocks
              <span 
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '12px'
                }}
              >
                {tests.filter(test => test.status === 'active').length}
              </span>
            </button>
            
            <button
              onClick={() => setActiveTab('past')}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderBottom: activeTab === 'past' ? '3px solid #007bff' : '3px solid transparent',
                backgroundColor: 'transparent',
                color: activeTab === 'past' ? '#007bff' : '#6c757d',
                fontWeight: activeTab === 'past' ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Past Mocks
              <span 
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '12px'
                }}
              >
                {tests.filter(test => test.status === 'closed').length}
              </span>
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'available' ? (
            // Available Mocks Tab Content
            tests.filter(test => test.status === 'active').length === 0 ? (
              <div className="text-center" style={{ padding: '40px', color: '#6c757d' }}>
                <h3>No Active Mocks Available</h3>
                <p>No active mock tests are currently available. Check back later!</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {tests
                  .filter(test => test.status === 'active')
                  .map((test) => (
                    <div key={test._id} className="card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <h3 style={{ margin: '0', color: '#333' }}>
                          {test.title}
                        </h3>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <span className="badge badge-secondary">
                            Week {test.week}
                          </span>
                          {(() => {
                            const statusInfo = getStatusInfo(test);
                            return statusInfo.badge ? (
                              <span 
                                className="badge" 
                                style={{ 
                                  backgroundColor: statusInfo.badge.color, 
                                  color: statusInfo.badge.textColor 
                                }}
                              >
                                {statusInfo.badge.text}
                              </span>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      
                      {test.description && (
                        <div style={{ color: '#666', marginBottom: '16px' }}>
                          <LaTeXRenderer text={test.description} />
                        </div>
                      )}
                      
                      <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                        <div><strong>Problems:</strong> {test.problems.length}</div>
                        <div><strong>Due Date:</strong> {formatDate(test.dueDate)}</div>
                        <div><strong>Created by:</strong> {test.createdBy.name}</div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <Link 
                          to={`/test/${test._id}`} 
                          className="btn btn-primary"
                          style={{ flex: 1 }}
                        >
                          View Mock & Submit Solutions
                        </Link>
                        
                        <button
                          onClick={() => handleDownloadPDF(test)}
                          className="btn btn-outline"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Download PDF
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )
          ) : (
            // Past Mocks Tab Content
            tests.filter(test => test.status === 'closed').length === 0 ? (
              <div className="text-center" style={{ padding: '40px', color: '#6c757d' }}>
                <h3>No Past Mocks</h3>
                <p>No past mock tests to display yet.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {tests
                  .filter(test => test.status === 'closed')
                  .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate)) // Sort by due date, most recent first
                  .map((test) => (
                    <div key={test._id} className="card" style={{ opacity: '0.85' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <h3 style={{ margin: '0', color: '#333' }}>
                          {test.title}
                        </h3>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <span className="badge badge-secondary">
                            Week {test.week}
                          </span>
                          {(() => {
                            const statusInfo = getStatusInfo(test);
                            return statusInfo.badge ? (
                              <span 
                                className="badge" 
                                style={{ 
                                  backgroundColor: statusInfo.badge.color, 
                                  color: statusInfo.badge.textColor 
                                }}
                              >
                                {statusInfo.badge.text}
                              </span>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      
                      {test.description && (
                        <div style={{ color: '#666', marginBottom: '16px' }}>
                          <LaTeXRenderer text={test.description} />
                        </div>
                      )}
                      
                      <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                        <div><strong>Problems:</strong> {test.problems.length}</div>
                        <div><strong>Due Date:</strong> {formatDate(test.dueDate)}</div>
                        <div><strong>Created by:</strong> {test.createdBy.name}</div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <Link 
                          to={`/test/${test._id}`} 
                          className="btn btn-outline"
                          style={{ flex: 1 }}
                        >
                          View Mock & Feedback
                        </Link>
                        
                        <button
                          onClick={() => handleDownloadPDF(test)}
                          className="btn btn-outline"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Download PDF
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )
          )}

          {/* Show message if no tests at all */}
          {tests.length === 0 && (
            <div className="text-center" style={{ padding: '40px', color: '#6c757d' }}>
              <h3>No Tests Available</h3>
              <p>No mock tests have been published yet. Check back later!</p>
            </div>
          )}
        </div>
      )}

      {/* Only show additional sections for approved users */}
      {user?.status !== 'pending_approval' && (
        <>
          {/* My Scores Dashboard */}
          <div className="card">
            <h2>📊 My Scores Dashboard</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Track your progress and scores across all mock tests. See your submission status and final scores for each problem.
            </p>
            
            <ScoreMatrix 
              user={user}
              isGraderView={false}
            />
          </div>

          {/* Only show grader request section for students who aren't already graders or admins */}
          {user && user.role === 'student' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0 }}>Want to Help Grade?</h3>
                <Link 
                  to="/request-grader" 
                  className="btn btn-primary"
                >
                  Request Grader Role
                </Link>
              </div>
              
              <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <p style={{ margin: '0 0 12px 0', color: '#495057' }}>
                  <strong>Become a grader and help fellow students!</strong>
                </p>
                <ul style={{ paddingLeft: '20px', margin: 0, color: '#6c757d', lineHeight: '1.5' }}>
                  <li>Provide feedback and help others improve their problem-solving and writing skills</li>
                  <li>Contribute to the learning community</li>
                </ul>
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
};

export default StudentDashboard;
